import React, { useEffect, useState, useCallback, useRef } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { setBranchId } from '../../../auth/actions';
import { setFiltersVisibility } from '../../../../common/actions';
import {
  getShiftSummary,
  getDailySummary,
  getDailyChart,
  getPaymentLedger,
  getPaymentLedgerMethods,
  getBusinessDayParticipation,
} from '../actions';
import { fetchSalesBusinessDays } from '../../sales/actions';
import { findBusinessDaySettings } from '../../../settings/businessDay/actions';
import store from '../../../../store';
import { Dropdown, Button } from '../../../../common/ui';
import PropTypes from 'prop-types';
import ShiftSummaryTable from './ShiftSummaryTable';
import DailySummaryTable from './DailySummaryTable';
import PaymentLedgerTable from './PaymentLedgerTable/index';
import DailyVarianceChart from './DailyVarianceChart';
import BusinessDayTable from './BusinessDayTable';
import CashBalanceFilterComp, { STATUS_OPTIONS } from './CashBalanceFilterComp';
import DateRangePicker from '../../../../common/components/DateRangePicker';
import { useSearchParams } from 'react-router-dom';
import { IoMdCloseCircleOutline } from 'react-icons/io';

const TAB_KEYS = {
  SHIFT: 'shift',
  DAILY: 'daily',
  LEDGER: 'ledger',
  BUSINESS_DAY: 'businessDay',
};

const TAB_LABELS = {
  [TAB_KEYS.SHIFT]: 'Shift Summary',
  [TAB_KEYS.DAILY]: 'Daily Summary',
  [TAB_KEYS.LEDGER]: 'Payment Ledger',
  [TAB_KEYS.BUSINESS_DAY]: 'Business Day',
};

const ZONE_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const startOfLocalDayMs = (ymd) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utc = Date.UTC(year, month - 1, day);
  const parsed = new Date(utc);
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }
  return utc - ZONE_OFFSET_MS;
};

const overlapsCalendarDate = (day, ymd) => {
  const startOfSpan = startOfLocalDayMs(ymd);
  if (startOfSpan == null) return false;
  const endOfSpan = startOfSpan + DAY_MS;
  const start = Number(day.startDatetime);
  if (!Number.isFinite(start)) return false;
  const end =
    day.endDatetime == null || day.endDatetime === '' ? null : Number(day.endDatetime);
  return start < endOfSpan && (end == null || end >= startOfSpan);
};

const defaultBusinessDayId = (days, ymd) => {
  const match = (days || [])
    .filter((day) => overlapsCalendarDate(day, ymd))
    .sort((a, b) => Number(a.startDatetime) - Number(b.startDatetime))[0];
  return match ? String(match.id) : '';
};

const formatBusinessDayOption = (day) => {
  const shifted = new Date(Number(day.startDatetime) + ZONE_OFFSET_MS);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let hour = shifted.getUTCHours();
  const minute = String(shifted.getUTCMinutes()).padStart(2, '0');
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour %= 12;
  if (hour === 0) hour = 12;
  const statusLabel = String(day.status || '').toUpperCase() === 'OPEN' ? 'Open' : 'Sealed';
  return `${shifted.getUTCDate()} ${months[shifted.getUTCMonth()]} ${shifted.getUTCFullYear()}, ${hour}:${minute} ${ampm} ${statusLabel}`;
};

const getThisMonthDates = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return {
    fromDate: firstDay.toLocaleDateString('en-CA'),
    toDate: lastDay.toLocaleDateString('en-CA'),
  };
};

const getTodayDates = () => {
  const todayDate = new Date().toLocaleDateString('en-CA');
  return {
    fromDate: todayDate,
    toDate: todayDate,
  };
};

const isKnownTab = (tab) => Object.values(TAB_KEYS).includes(tab);

const toPaymentMethodFilter = (method) => {
  if (!method?.id) return null;
  return {
    id: method.id,
    name: method.name,
    typeName: method.typeName,
  };
};

const findCashMethod = (methods = []) =>
  methods.find((method) => method.isCash) ||
  methods.find((method) => String(method.typeName).trim().toLowerCase() === 'cash') ||
  null;

const CashBalanceReport = ({
  setBranchId,
  setFiltersVisibility,
  getShiftSummary,
  getDailySummary,
  getDailyChart,
  getPaymentLedger,
  getPaymentLedgerMethods,
  getBusinessDayParticipation,
  findBusinessDaySettings,
  auth: { branchList = [], branchId, companyId },
  cashBalanceState: {
    isGetShiftSummaryLoading,
    shiftSummaryData,
    totalRecords,
    currentPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    getShiftSummaryError,
    isGetDailySummaryLoading,
    dailySummaryData,
    dailyTotalRecords,
    dailyCurrentPage,
    dailyTotalPages,
    dailyHasNextPage,
    dailyHasPreviousPage,
    getDailySummaryError,
    isGetDailyChartLoading,
    dailyChartData,
    getDailyChartError,
    isGetPaymentLedgerLoading,
    paymentLedgerData,
    paymentLedgerTotalRecords,
    paymentLedgerCurrentPage,
    paymentLedgerTotalPages,
    paymentLedgerHasNextPage,
    paymentLedgerHasPreviousPage,
    getPaymentLedgerError,
    paymentLedgerMethods,
    defaultPaymentLedgerMethodId,
    businessDayData,
    businessDayTotalRecords,
    businessDayCurrentPage,
    businessDayTotalPages,
    businessDayHasNextPage,
    businessDayHasPreviousPage,
    isGetBusinessDayLoading,
    getBusinessDayError,
  },
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({});
  const tabParam = searchParams.get('tab');
  const activeTab = isKnownTab(tabParam) ? tabParam : TAB_KEYS.SHIFT;
  const isShiftTab = activeTab === TAB_KEYS.SHIFT;
  const isDailyTab = activeTab === TAB_KEYS.DAILY;
  const isLedgerTab = activeTab === TAB_KEYS.LEDGER;
  const isBusinessDayTab = activeTab === TAB_KEYS.BUSINESS_DAY;
  const defaultDates = isLedgerTab ? getTodayDates() : getThisMonthDates();
  const periodType = searchParams.get('periodType') || '';
  const startBusinessDayId = searchParams.get('startBusinessDayId') || '';
  const endBusinessDayId = searchParams.get('endBusinessDayId') || '';
  const [businessDayEnabled, setBusinessDayEnabled] = useState(null);
  const [businessDaySettingsError, setBusinessDaySettingsError] = useState('');
  const [businessDays, setBusinessDays] = useState([]);
  const [businessDaysLoaded, setBusinessDaysLoaded] = useState(false);
  const spanRef = useRef('');
  const branchRef = useRef(branchId);

  const page = parseInt(searchParams.get('page'), 10) || 1;
  const limit = parseInt(searchParams.get('limit'), 10) || 10;
  let fromDate = searchParams.get('fromDate') || defaultDates.fromDate;
  let toDate = searchParams.get('toDate') || defaultDates.toDate;

  if (!fromDate || !toDate) {
    const todayDate = new Date().toLocaleDateString('en-CA');
    fromDate = fromDate || todayDate;
    toDate = toDate || todayDate;
  }

  const businessDayMode = businessDayEnabled === true && periodType === 'businessDay';
  const listedIds = new Set((businessDays || []).map((day) => String(day.id)));
  const selectorsReady =
    Boolean(startBusinessDayId) &&
    Boolean(endBusinessDayId) &&
    listedIds.has(String(startBusinessDayId)) &&
    listedIds.has(String(endBusinessDayId));
  const reportReady =
    businessDayEnabled === false ||
    (businessDayEnabled === true && periodType === 'dateRange') ||
    (businessDayMode && selectorsReady);

  const appendBusinessDayParams = useCallback(
    (params) => {
      if (businessDayMode && selectorsReady) {
        params.periodType = 'businessDay';
        params.startBusinessDayId = startBusinessDayId;
        params.endBusinessDayId = endBusinessDayId;
      }
      return params;
    },
    [businessDayMode, selectorsReady, startBusinessDayId, endBusinessDayId]
  );

  const buildFetchParams = useCallback(() => {
    const params = appendBusinessDayParams({
      fromDate,
      toDate,
      page,
      limit,
    });
    if (filters.cashier?.id != null) {
      params.cashierRegistryId = filters.cashier.id;
    }
    if (isShiftTab && filters.user?.id != null) {
      params.userId = filters.user.id;
    }
    if (!isBusinessDayTab && filters.status) {
      params.status = filters.status;
    }
    return params;
  }, [
    appendBusinessDayParams,
    fromDate,
    toDate,
    page,
    limit,
    filters,
    isShiftTab,
    isBusinessDayTab,
  ]);

  const buildChartFetchParams = useCallback(() => {
    const params = appendBusinessDayParams({
      fromDate,
      toDate,
    });
    if (filters.cashier?.id != null) {
      params.cashierRegistryId = filters.cashier.id;
    }
    if (filters.status) {
      params.status = filters.status;
    }
    return params;
  }, [appendBusinessDayParams, fromDate, toDate, filters]);

  const branchOptions = (branchList || []).map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const handleChangeBranch = (value) => {
    setBranchId(value);
  };

  const handleDateRangeChange = (dateRange) => {
    if (dateRange?.startDate && dateRange?.endDate) {
      const from = dateRange.startDate.toLocaleDateString('en-CA');
      const to = dateRange.endDate.toLocaleDateString('en-CA');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('fromDate', from);
        next.set('toDate', to);
        next.set('page', '1');
        return next;
      });
    } else {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('fromDate');
        next.delete('toDate');
        next.set('page', '1');
        return next;
      });
    }
  };

  const handleTabChange = (tabKey) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const previousTab = isKnownTab(prev.get('tab')) ? prev.get('tab') : TAB_KEYS.SHIFT;
      next.set('page', '1');
      next.set('tab', tabKey);
      if (tabKey === TAB_KEYS.LEDGER && previousTab !== TAB_KEYS.LEDGER) {
        const today = getTodayDates();
        next.set('fromDate', today.fromDate);
        next.set('toDate', today.toDate);
      } else if (previousTab === TAB_KEYS.LEDGER && tabKey !== TAB_KEYS.LEDGER) {
        const month = getThisMonthDates();
        next.set('fromDate', month.fromDate);
        next.set('toDate', month.toDate);
      }
      return next;
    });
    if (tabKey === TAB_KEYS.DAILY && filters.user) {
      setFilters((prev) => {
        const next = { ...prev };
        delete next.user;
        return next;
      });
    }
  };

  useEffect(() => {
    const hasPage = searchParams.has('page');
    const hasLimit = searchParams.has('limit');
    const hasFromDate = searchParams.has('fromDate');
    const hasToDate = searchParams.has('toDate');
    if (!hasPage || !hasLimit || !hasFromDate || !hasToDate) {
      const dates =
        searchParams.get('tab') === TAB_KEYS.LEDGER
          ? getTodayDates()
          : getThisMonthDates();
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (!hasPage) next.set('page', '1');
        if (!hasLimit) next.set('limit', '10');
        if (!hasFromDate) next.set('fromDate', dates.fromDate);
        if (!hasToDate) next.set('toDate', dates.toDate);
        return next;
      }, { replace: true });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!branchId) {
      setBusinessDayEnabled(false);
      return undefined;
    }
    setBusinessDayEnabled(null);
    setBusinessDaySettingsError('');
    findBusinessDaySettings(branchId).then((ok) => {
      if (cancelled) return;
      if (!ok) {
        setBusinessDaySettingsError('Unable to load Business Day settings.');
        return;
      }
      const rows = store.getState().businessDaySettingsState?.businessDaySettingsData || [];
      const enabled = rows.some(
        (row) =>
          row.param === 'is-business-day-enabled' &&
          String(row.value).toLowerCase() === 'true'
      );
      setBusinessDayEnabled(enabled);
      setBusinessDaySettingsError('');
    });
    return () => {
      cancelled = true;
    };
  }, [branchId, findBusinessDaySettings]);

  useEffect(() => {
    if (businessDayEnabled == null) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!businessDayEnabled) {
        let changed = false;
        ['periodType', 'startBusinessDayId', 'endBusinessDayId'].forEach((key) => {
          if (next.has(key)) {
            next.delete(key);
            changed = true;
          }
        });
        if (next.get('tab') === TAB_KEYS.BUSINESS_DAY) {
          next.set('tab', TAB_KEYS.SHIFT);
          changed = true;
        }
        return changed ? next : prev;
      }
      const period = next.get('periodType');
      if (period !== 'businessDay' && period !== 'dateRange') {
        next.set('periodType', 'businessDay');
        next.delete('startBusinessDayId');
        next.delete('endBusinessDayId');
        return next;
      }
      return prev;
    });
  }, [businessDayEnabled, branchId, setSearchParams]);

  useEffect(() => {
    if (branchRef.current === branchId) return;
    branchRef.current = branchId;
    setBusinessDays([]);
    setBusinessDaysLoaded(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('startBusinessDayId');
      next.delete('endBusinessDayId');
      return next;
    });
  }, [branchId, setSearchParams]);

  useEffect(() => {
    const spanKey = `${fromDate}|${toDate}`;
    if (!spanRef.current) {
      spanRef.current = spanKey;
      return;
    }
    if (spanRef.current === spanKey) return;
    spanRef.current = spanKey;
    if (periodType !== 'businessDay') return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('startBusinessDayId');
      next.delete('endBusinessDayId');
      return next;
    });
  }, [fromDate, toDate, periodType, setSearchParams]);

  useEffect(() => {
    if (!businessDayMode || !companyId || !branchId) {
      setBusinessDays([]);
      setBusinessDaysLoaded(false);
      return undefined;
    }
    let cancelled = false;
    setBusinessDaysLoaded(false);
    fetchSalesBusinessDays({ fromDate, toDate })
      .then((days) => {
        if (cancelled) return;
        const list = days || [];
        setBusinessDays(list);
        setBusinessDaysLoaded(true);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          let changed = false;
          const ids = new Set(list.map((day) => String(day.id)));
          ['startBusinessDayId', 'endBusinessDayId'].forEach((key) => {
            const current = next.get(key);
            if (current && !ids.has(current)) {
              next.delete(key);
              changed = true;
            }
          });
          if (!next.get('startBusinessDayId')) {
            const id = defaultBusinessDayId(list, fromDate);
            if (id) {
              next.set('startBusinessDayId', id);
              changed = true;
            }
          }
          if (!next.get('endBusinessDayId')) {
            const id = defaultBusinessDayId(list, toDate);
            if (id) {
              next.set('endBusinessDayId', id);
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      })
      .catch(() => {
        if (cancelled) return;
        setBusinessDays([]);
        setBusinessDaysLoaded(true);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('startBusinessDayId');
          next.delete('endBusinessDayId');
          return next;
        });
      });
    return () => {
      cancelled = true;
    };
  }, [businessDayMode, companyId, branchId, fromDate, toDate, setSearchParams]);

  useEffect(() => {
    if (!isBusinessDayTab) return;
    if (businessDayEnabled === false || periodType === 'dateRange') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', TAB_KEYS.SHIFT);
        return next;
      });
    }
  }, [isBusinessDayTab, businessDayEnabled, periodType, setSearchParams]);

  useEffect(() => {
    if (!companyId || !branchId || !reportReady) return;
    if (isShiftTab) {
      getShiftSummary(buildFetchParams());
    } else if (isDailyTab) {
      getDailySummary(buildFetchParams());
    }
  }, [
    companyId,
    branchId,
    isShiftTab,
    isDailyTab,
    reportReady,
    buildFetchParams,
    getShiftSummary,
    getDailySummary,
  ]);

  useEffect(() => {
    if (!companyId || !branchId || !isDailyTab || !reportReady) return;
    getDailyChart(buildChartFetchParams());
  }, [
    companyId,
    branchId,
    isDailyTab,
    reportReady,
    buildChartFetchParams,
    getDailyChart,
  ]);

  useEffect(() => {
    if (!companyId || !branchId || !isLedgerTab || !reportReady) return;
    getPaymentLedgerMethods(appendBusinessDayParams({ fromDate, toDate }));
  }, [
    companyId,
    branchId,
    isLedgerTab,
    reportReady,
    fromDate,
    toDate,
    appendBusinessDayParams,
    getPaymentLedgerMethods,
  ]);

  useEffect(() => {
    if (!isLedgerTab || !(paymentLedgerMethods || []).length) return;
    const cashMethod =
      findCashMethod(paymentLedgerMethods) ||
      (paymentLedgerMethods || []).find(
        (method) => String(method.id) === String(defaultPaymentLedgerMethodId)
      );
    if (!cashMethod) return;

    setFilters((prev) => {
      const currentId = prev.paymentMethod?.id;
      const stillValid =
        currentId &&
        (paymentLedgerMethods || []).some(
          (method) => String(method.id) === String(currentId)
        );
      if (stillValid) return prev;
      const nextMethod = toPaymentMethodFilter(cashMethod);
      if (
        String(prev.paymentMethod?.id) === String(nextMethod.id) &&
        prev.paymentMethod?.name === nextMethod.name
      ) {
        return prev;
      }
      return { ...prev, paymentMethod: nextMethod };
    });
  }, [isLedgerTab, paymentLedgerMethods, defaultPaymentLedgerMethodId]);

  useEffect(() => {
    if (!companyId || !branchId || !isLedgerTab || !reportReady) return;
    if (!filters.paymentMethod?.id) return;
    getPaymentLedger(
      appendBusinessDayParams({
        fromDate,
        toDate,
        page,
        limit,
        companyPaymentMethodId: filters.paymentMethod.id,
      })
    );
  }, [
    companyId,
    branchId,
    isLedgerTab,
    reportReady,
    fromDate,
    toDate,
    page,
    limit,
    filters.paymentMethod?.id,
    appendBusinessDayParams,
    getPaymentLedger,
  ]);

  useEffect(() => {
    if (!companyId || !branchId || !isBusinessDayTab || !reportReady) return;
    getBusinessDayParticipation(buildFetchParams());
  }, [
    companyId,
    branchId,
    isBusinessDayTab,
    reportReady,
    buildFetchParams,
    getBusinessDayParticipation,
  ]);

  const handleFiltersClick = () => {
    setFiltersVisibility(true);
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', '1');
      return next;
    });
  };

  const handleRemoveFilter = (key) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (isLedgerTab && key === 'paymentMethod') {
        const cashMethod = findCashMethod(paymentLedgerMethods);
        if (cashMethod) {
          next.paymentMethod = toPaymentMethodFilter(cashMethod);
        } else {
          delete next.paymentMethod;
        }
        return next;
      }
      delete next[key];
      return next;
    });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', '1');
      return next;
    });
  };

  const handleResetFilters = () => {
    setFilters((prev) => {
      if (isLedgerTab) {
        const cashMethod = findCashMethod(paymentLedgerMethods);
        const next = { ...prev };
        if (cashMethod) {
          next.paymentMethod = toPaymentMethodFilter(cashMethod);
        } else {
          delete next.paymentMethod;
        }
        return next;
      }
      const next = { ...prev };
      delete next.cashier;
      delete next.user;
      delete next.status;
      return next;
    });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', '1');
      return next;
    });
  };

  const filterTagLabel = (key, value) => {
    if (key === 'cashier') {
      return `Cashier Point: ${value.name || value.cashierNo || value.id}`;
    }
    if (key === 'user') return `User: ${value.name || value.id}`;
    if (key === 'status') {
      const label =
        STATUS_OPTIONS.find((option) => option.value === value)?.label || value;
      return `Status: ${label}`;
    }
    if (key === 'paymentMethod') {
      return `Payment Method: ${value.name || value.typeName || value.id}`;
    }
    return `${key}: ${value}`;
  };

  const tabLabel = TAB_LABELS[activeTab];
  const breadcrumb = `Reports / Cash Balance / ${tabLabel}`;

  const shiftPagination = {
    totalRecords,
    currentPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
  };

  const dailyPagination = {
    totalRecords: dailyTotalRecords,
    currentPage: dailyCurrentPage,
    totalPages: dailyTotalPages,
    hasNextPage: dailyHasNextPage,
    hasPreviousPage: dailyHasPreviousPage,
  };

  const ledgerPagination = {
    totalRecords: paymentLedgerTotalRecords,
    currentPage: paymentLedgerCurrentPage,
    totalPages: paymentLedgerTotalPages,
    hasNextPage: paymentLedgerHasNextPage,
    hasPreviousPage: paymentLedgerHasPreviousPage,
  };

  const businessDayPagination = {
    totalRecords: businessDayTotalRecords,
    currentPage: businessDayCurrentPage,
    totalPages: businessDayTotalPages,
    hasNextPage: businessDayHasNextPage,
    hasPreviousPage: businessDayHasPreviousPage,
  };

  const visibleFilters = (() => {
    if (isLedgerTab) {
      return Object.fromEntries(
        Object.entries(filters).filter(([key]) => key === 'paymentMethod')
      );
    }
    if (isBusinessDayTab) {
      return Object.fromEntries(
        Object.entries(filters).filter(([key]) => key === 'cashier')
      );
    }
    if (isDailyTab) {
      return Object.fromEntries(
        Object.entries(filters).filter(
          ([key]) => key !== 'user' && key !== 'paymentMethod'
        )
      );
    }
    return Object.fromEntries(
      Object.entries(filters).filter(([key]) => key !== 'paymentMethod')
    );
  })();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>Cash Balance</h1>
            <p className={styles.breadcrumb}>{breadcrumb}</p>
          </div>
          {branchId != null && branchOptions.length > 0 && (
            <div className={styles.branchWrap}>
              <Dropdown
                label="Branch"
                options={branchOptions}
                value={branchId}
                onChange={handleChangeBranch}
                placeholder="Select branch"
              />
            </div>
          )}
        </div>
        <div className={styles.addedFiltersDiv}>
          {Object.entries(visibleFilters).map(([key, value]) => (
            <span key={key} className={styles.filterTag}>
              {filterTagLabel(key, value)}
              <button
                type="button"
                className={styles.removeFilterBtn}
                onClick={() => handleRemoveFilter(key)}
                aria-label="Remove filter"
              >
                <IoMdCloseCircleOutline />
              </button>
            </span>
          ))}
          {Object.keys(visibleFilters).length > 0 && (
            <button
              type="button"
              className={styles.resetFiltersBtn}
              onClick={handleResetFilters}
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      <div className={styles.tabsRow}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === TAB_KEYS.SHIFT ? styles.tabActive : ''}`}
          onClick={() => handleTabChange(TAB_KEYS.SHIFT)}
        >
          Shift Summary
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === TAB_KEYS.DAILY ? styles.tabActive : ''}`}
          onClick={() => handleTabChange(TAB_KEYS.DAILY)}
        >
          Daily Summary
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === TAB_KEYS.LEDGER ? styles.tabActive : ''}`}
          onClick={() => handleTabChange(TAB_KEYS.LEDGER)}
        >
          Payment Ledger
        </button>
        {businessDayMode && (
          <button
            type="button"
            className={`${styles.tab} ${activeTab === TAB_KEYS.BUSINESS_DAY ? styles.tabActive : ''}`}
            onClick={() => handleTabChange(TAB_KEYS.BUSINESS_DAY)}
          >
            Business Day
          </button>
        )}
      </div>

      <div className={styles.body}>
        <div className={styles.card}>
          <div className={styles.toolbar}>
            <div className={styles.toolbarRight}>
              <div className={styles.dateRangeWrap}>
                <DateRangePicker
                  onDateRangeChange={handleDateRangeChange}
                  initialStartDate={new Date(fromDate)}
                  initialEndDate={new Date(toDate)}
                  businessDay={
                    businessDayEnabled
                      ? {
                          periodType: periodType || 'businessDay',
                          onPeriodTypeChange: (value) => {
                            setSearchParams((prev) => {
                              const next = new URLSearchParams(prev);
                              next.set('periodType', value);
                              next.delete('startBusinessDayId');
                              next.delete('endBusinessDayId');
                              if (
                                value !== 'businessDay' &&
                                next.get('tab') === TAB_KEYS.BUSINESS_DAY
                              ) {
                                next.set('tab', TAB_KEYS.SHIFT);
                              }
                              return next;
                            });
                          },
                          options: businessDays.map((day) => ({
                            id: String(day.id),
                            label: formatBusinessDayOption(day),
                          })),
                          startId: startBusinessDayId,
                          endId: endBusinessDayId,
                          onStartChange: (id) => {
                            setSearchParams((prev) => {
                              const next = new URLSearchParams(prev);
                              next.set('startBusinessDayId', id);
                              next.set('page', '1');
                              return next;
                            });
                          },
                          onEndChange: (id) => {
                            setSearchParams((prev) => {
                              const next = new URLSearchParams(prev);
                              next.set('endBusinessDayId', id);
                              next.set('page', '1');
                              return next;
                            });
                          },
                          empty: businessDaysLoaded && businessDays.length === 0,
                        }
                      : null
                  }
                />
              </div>
              <Button variant="secondary" onClick={handleFiltersClick}>
                Filters
              </Button>
            </div>
          </div>
          <CashBalanceFilterComp
            filters={filters}
            setFilters={handleApplyFilters}
            showUserFilter={isShiftTab}
            showCashierFilter={!isLedgerTab}
            showStatusFilter={!isLedgerTab && !isBusinessDayTab}
            showPaymentMethodFilter={isLedgerTab}
          />
          <div className={styles.tableWrap}>
            {isShiftTab && (
              <ShiftSummaryTable
                data={shiftSummaryData}
                isLoading={isGetShiftSummaryLoading}
                error={getShiftSummaryError}
                pagination={shiftPagination}
                setSearchParams={setSearchParams}
                page={page}
                limit={limit}
              />
            )}
            {isDailyTab && (
              <DailySummaryTable
                data={dailySummaryData}
                isLoading={isGetDailySummaryLoading}
                error={getDailySummaryError}
                pagination={dailyPagination}
                setSearchParams={setSearchParams}
                page={page}
                limit={limit}
              />
            )}
            {businessDaySettingsError && (
              <p role="alert">{businessDaySettingsError}</p>
            )}
            {isBusinessDayTab && (
              <BusinessDayTable
                data={businessDayData}
                isLoading={isGetBusinessDayLoading || !reportReady}
                error={getBusinessDayError}
                pagination={businessDayPagination}
                setSearchParams={setSearchParams}
                page={page}
                limit={limit}
              />
            )}
            {isLedgerTab && (
              <PaymentLedgerTable
                data={paymentLedgerData}
                isLoading={isGetPaymentLedgerLoading}
                error={getPaymentLedgerError}
                pagination={ledgerPagination}
                setSearchParams={setSearchParams}
                page={page}
                limit={limit}
              />
            )}
          </div>
          {isDailyTab && (
            <DailyVarianceChart
              data={dailyChartData}
              isLoading={isGetDailyChartLoading}
              error={getDailyChartError}
            />
          )}
        </div>
      </div>
    </div>
  );
};

CashBalanceReport.propTypes = {
  setBranchId: PropTypes.func.isRequired,
  setFiltersVisibility: PropTypes.func.isRequired,
  getShiftSummary: PropTypes.func.isRequired,
  getDailySummary: PropTypes.func.isRequired,
  getDailyChart: PropTypes.func.isRequired,
  getPaymentLedger: PropTypes.func.isRequired,
  getPaymentLedgerMethods: PropTypes.func.isRequired,
  getBusinessDayParticipation: PropTypes.func.isRequired,
  findBusinessDaySettings: PropTypes.func.isRequired,
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  cashBalanceState: PropTypes.shape({
    isGetShiftSummaryLoading: PropTypes.bool,
    shiftSummaryData: PropTypes.array,
    totalRecords: PropTypes.number,
    currentPage: PropTypes.number,
    totalPages: PropTypes.number,
    hasNextPage: PropTypes.bool,
    hasPreviousPage: PropTypes.bool,
    getShiftSummaryError: PropTypes.string,
    isGetDailySummaryLoading: PropTypes.bool,
    dailySummaryData: PropTypes.array,
    dailyTotalRecords: PropTypes.number,
    dailyCurrentPage: PropTypes.number,
    dailyTotalPages: PropTypes.number,
    dailyHasNextPage: PropTypes.bool,
    dailyHasPreviousPage: PropTypes.bool,
    getDailySummaryError: PropTypes.string,
    isGetDailyChartLoading: PropTypes.bool,
    dailyChartData: PropTypes.shape({
      dates: PropTypes.array,
      series: PropTypes.array,
    }),
    getDailyChartError: PropTypes.string,
    isGetPaymentLedgerLoading: PropTypes.bool,
    paymentLedgerData: PropTypes.array,
    paymentLedgerTotalRecords: PropTypes.number,
    paymentLedgerCurrentPage: PropTypes.number,
    paymentLedgerTotalPages: PropTypes.number,
    paymentLedgerHasNextPage: PropTypes.bool,
    paymentLedgerHasPreviousPage: PropTypes.bool,
    getPaymentLedgerError: PropTypes.string,
    paymentLedgerMethods: PropTypes.array,
    defaultPaymentLedgerMethodId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    businessDayData: PropTypes.array,
    businessDayTotalRecords: PropTypes.number,
    businessDayCurrentPage: PropTypes.number,
    businessDayTotalPages: PropTypes.number,
    businessDayHasNextPage: PropTypes.bool,
    businessDayHasPreviousPage: PropTypes.bool,
    isGetBusinessDayLoading: PropTypes.bool,
    getBusinessDayError: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.authState,
  cashBalanceState: state.cashBalanceState,
});

export default connect(mapStateToProps, {
  setBranchId,
  setFiltersVisibility,
  getShiftSummary,
  getDailySummary,
  getDailyChart,
  getPaymentLedger,
  getPaymentLedgerMethods,
  getBusinessDayParticipation,
  findBusinessDaySettings,
})(CashBalanceReport);
